/**
 * BlobUrlResolver - Transforms blob IDs into loadable URLs
 *
 * SOLID:
 * - S: Single Responsibility - Only handles blob ID to URL transformation
 * - O: Open/Closed - New blob types can be added without modifying existing code
 *
 * Background:
 * When images are uploaded to the chat, the backend stores them with blob IDs
 * like "blob-1769289607048-sessionId". These IDs need to be transformed into
 * actual URLs that the browser can load.
 *
 * The blob endpoint is: /api/workspaces/{workspaceId}/blobs/{blobId}
 * For copilot attachments, workspaceId is always 'copilot'.
 */

/**
 * Checks if a string is a blob ID that needs URL transformation
 *
 * Blob IDs follow the pattern: blob-{timestamp}-{filename}
 * Example: blob-1769289607048-e79f5579-7598-43cc-850d-b1aa13d8e544
 */
export function isBlobId(value: string): boolean {
  return value.startsWith('blob-');
}

const IMAGE_DATA_URL_PREFIX_RE = /^data:image\/[a-z0-9.+-]+;base64,/i;
const IMAGE_DATA_URL_RE = /data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+/gi;
const LEGACY_CANVAS_CONTEXT_REFERENCE_SUFFIX_RE =
  /\n{2}Canvas context references \(data only\):[^\n]*\s*$/;

export const MAX_INLINE_IMAGE_DATA_URL_LENGTH = 128 * 1024;
export const OMITTED_INLINE_IMAGE_PLACEHOLDER =
  'streamlain:omitted-large-inline-image';
export const OMITTED_INLINE_IMAGE_TEXT =
  '[Generated image omitted from text preview]';

export function isInlineImageDataUrl(value: string): boolean {
  return IMAGE_DATA_URL_PREFIX_RE.test(value);
}

export function isLargeInlineImageDataUrl(value: string): boolean {
  return (
    value.length > MAX_INLINE_IMAGE_DATA_URL_LENGTH &&
    isInlineImageDataUrl(value)
  );
}

export function isOmittedInlineImagePlaceholder(value: string): boolean {
  return value === OMITTED_INLINE_IMAGE_PLACEHOLDER;
}

export function sanitizeChatMessageContentForDisplay(content: string): string {
  const visibleContent = content.replace(
    LEGACY_CANVAS_CONTEXT_REFERENCE_SUFFIX_RE,
    ''
  );
  if (
    visibleContent.length <= MAX_INLINE_IMAGE_DATA_URL_LENGTH ||
    !visibleContent.includes('data:image/')
  ) {
    return visibleContent;
  }

  return visibleContent.replace(IMAGE_DATA_URL_RE, match =>
    match.length > MAX_INLINE_IMAGE_DATA_URL_LENGTH
      ? OMITTED_INLINE_IMAGE_TEXT
      : match
  );
}

function sanitizeImageUrlForDisplay(value: string): string {
  return isLargeInlineImageDataUrl(value)
    ? OMITTED_INLINE_IMAGE_PLACEHOLDER
    : value;
}

/**
 * Checks if a string is already a valid URL (doesn't need transformation)
 */
export function isUrl(value: string): boolean {
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('data:') ||
    value.startsWith('blob:')
  );
}

/**
 * Resolves a blob ID to a loadable URL
 *
 * @param blobId - The blob ID to resolve (e.g., "blob-1769289607048-...")
 * @param workspaceId - The workspace ID (default: 'copilot' for chat attachments)
 * @returns The full URL to load the blob
 */
export function resolveBlobUrl(
  blobId: string,
  workspaceId: string = 'copilot'
): string {
  if (isLargeInlineImageDataUrl(blobId)) {
    return OMITTED_INLINE_IMAGE_PLACEHOLDER;
  }

  // Already a URL - return as-is
  if (isUrl(blobId)) {
    return blobId;
  }

  // Transform blob ID to API URL
  // The blob endpoint requires authentication, so we use relative URL
  return `/api/workspaces/${workspaceId}/blobs/${encodeURIComponent(blobId)}`;
}

/**
 * Transforms all attachments in a message from blob IDs to URLs
 *
 * @param attachments - Array of attachment strings (blob IDs or URLs)
 * @param workspaceId - The workspace ID for blob resolution
 * @returns Array of resolved URLs (preserves null if input was null)
 */
export function resolveAttachments(
  attachments: string[] | undefined | null,
  workspaceId: string = 'copilot'
): string[] | null {
  if (!attachments || attachments.length === 0) {
    return attachments ?? null;
  }

  return attachments.map(attachment =>
    sanitizeImageUrlForDisplay(resolveBlobUrl(attachment, workspaceId))
  );
}

type HistoryMessageLike = {
  content?: string;
  attachments?: string[] | null;
  messages?: HistoryMessageLike[];
  streamObjects?: Array<
    | { type: 'text-delta' | 'reasoning'; textDelta: string }
    | Record<string, unknown>
  > | null;
};

function sanitizeHistoryMessageForDisplay<T extends HistoryMessageLike>(
  message: T,
  workspaceId: string
): T {
  let changed = false;
  const nextMessage: HistoryMessageLike = { ...message };

  if (typeof message.content === 'string') {
    const content = sanitizeChatMessageContentForDisplay(message.content);
    if (content !== message.content) {
      nextMessage.content = content;
      changed = true;
    }
  }

  if (message.attachments?.length) {
    const attachments = resolveAttachments(message.attachments, workspaceId);
    if (attachments !== message.attachments) {
      nextMessage.attachments = attachments;
      changed = true;
    }
  }

  if (message.streamObjects?.length) {
    const streamObjects = message.streamObjects.map(streamObject => {
      if (
        (streamObject.type === 'text-delta' ||
          streamObject.type === 'reasoning') &&
        typeof streamObject.textDelta === 'string'
      ) {
        const textDelta = sanitizeChatMessageContentForDisplay(
          streamObject.textDelta
        );
        if (textDelta !== streamObject.textDelta) {
          changed = true;
          return { ...streamObject, textDelta };
        }
      }
      return streamObject;
    });
    nextMessage.streamObjects = streamObjects;
  }

  if (message.messages?.length) {
    nextMessage.messages = message.messages.map(child =>
      sanitizeHistoryMessageForDisplay(child, workspaceId)
    );
    changed = true;
  }

  return (changed ? nextMessage : message) as T;
}

/**
 * Transforms all message attachments in a history entry
 *
 * This function is designed to be used in the history loading pipeline
 * to ensure all attachments are resolved before rendering.
 *
 * Note: Uses type assertion because we're only changing attachment values,
 * not the structure. The TypeScript generic constraint ensures type safety.
 *
 * @param history - A history entry with messages
 * @param workspaceId - The workspace ID for blob resolution
 * @returns The history entry with resolved attachment URLs
 */
export function transformHistoryAttachments<
  T extends { messages?: HistoryMessageLike[] },
>(history: T, workspaceId: string = 'copilot'): T {
  if (!history.messages) {
    return history;
  }

  const transformedMessages = history.messages.map(message =>
    sanitizeHistoryMessageForDisplay(message, workspaceId)
  );

  return {
    ...history,
    messages: transformedMessages,
  } as T;
}
